/**
 * @author EdenCha <eden@nurigo.net>
 */
import React, { Component } from 'react'
import { connect } from 'react-redux'
import autoBind from 'auto-bind'
import { withRouter } from 'react-router-dom'
import copy from 'copy-to-clipboard'
import jwt from 'jsonwebtoken'
import cookies from 'js-cookie'
import pick from 'lodash/pick'
import debounce from 'lodash/debounce'

// lib
import bindStateToProps from 'lib/bindStateToProps'
import bindActionCreators from 'lib/bindActionCreators'
import isEqualProps from 'lib/isEqualProps'
import mapActionLog from 'lib/mapActionLog'
import proxyOverride from 'lib/proxyOverride'
import mapAsyncActions from 'lib/mapAsyncActions'
import getCookieDomain from 'lib/getCookieDomain'
import { isArray, isObject, isEmptyObject, isEmptyString } from 'lib/detectType'

// redux modules (action)
// import { actions as rbcActions } from 'store/modules/rbc'
import { actions as notificationActions } from 'store/modules/notification'

// common modules (action)
import { actions as formActions } from 'store/modules/form'
// import { actions as tableActions } from 'store/modules/table'
// import { actions as searchActions } from 'store/modules/search'
// import { actions as uploadActions } from 'store/modules/upload'
import { actions as popupActions } from 'store/modules/popup'
import { actions as snackbarActions } from 'store/modules/snackbar'
import { actions as confirmActions } from 'store/modules/confirm'
// import { actions as helpActions } from 'store/modules/help'

// react components & containers
import LayoutHeaderMenu from 'components/organisms/LayoutHeaderMenu'
import LayoutHeaderNotificationContainer from 'containers/LayoutHeaderNotificationContainer'
import CreateExclusiveAccountContainer from 'containers/CreateExclusiveAccountContainer'
import AccountBusinessInfoContainer from 'containers/AccountBusinessInfoContainer'
import RechargeContainer from 'containers/RechargeContainer'

import config from 'config'

/**
 * 레이아웃 우측 상단 계정메뉴 컨테이너
 */
class LayoutHeaderMenuContainer extends Component {
  constructor(props) {
    super(props)
    // 해당 컨테이너 내의 메서드에 자동으로 this 클래스를 바인딩 합니다.
    autoBind(this)
    // 해당 컨테이너 내의 메서드 실행 시 액션로그를 남깁니다.
    mapActionLog(this)
    // HOC 사용 시 현재 컨테이너에 proxy가 props에 존재하는 경우 proxy 메서드에 this 클래스의 메서드를 대입
    proxyOverride(this)
    // 2차 인증을 사용하는 리덕스 액션이 존재하는 경우 덮어씌워 await이 정상적으로 작동하게 합니다.
    mapAsyncActions(this.props, {
      // exceptActions: { AppsActions: true }
    })
    this.anchorRef = React.createRef()
    // 페이지 이동시 메뉴 닫음
    this.props.history.listen((location, action) => {
      this.onChangeFormData({ popperOpen: false })
    })
  }

  // hoc 사용 시 해당 부분 삭제하세요.
  componentDidMount = () => {
    this.initForm()
    this.initData()
  }

  shouldComponentUpdate = nextProps => {
    return !isEqualProps(nextProps, this.props, [
      'auth',
      'formData',
      'deferredPayment',
      'getBalance',
      'getBankCode',
      'getEstimateQuota',
      'getExclusiveAccount',
      'getNotifications'
    ])
  }

  componentDidUpdate = prevProps => {
    const filteredObjectKeys = [
      'deferredPayment',
      'getBalance',
      'getBankCode',
      'getEstimateQuota',
      'getExclusiveAccount',
      'getNotifications'
    ]
    const filteredResponse = Object.values(
      pick(this.props, filteredObjectKeys)
    ).filter(data => {
      return (
        isObject(data) &&
        !isEmptyObject(data) &&
        (data?.loading !== undefined ? data?.loading === false : true)
      )
    })
    if (
      !isEqualProps(this.props, prevProps, 'auth') ||
      (filteredResponse.length === filteredObjectKeys.length &&
        !isEqualProps(this.props, prevProps, filteredObjectKeys))
    ) {
      this.initData()
    }
  }

  initForm = () => {
    const { FormActions, auth } = this.props
    FormActions.init({
      key: 'layoutHeaderMenu',
      data: { popperOpen: false, auth }
    })
  }

  initData = debounce(async () => {
    const { FormActions, auth } = this.props
    if (!isObject(auth) || isEmptyObject(auth)) {
      return
    }
    const notiFormData = await this.getNotifications()
    FormActions.set({
      key: 'layoutHeaderMenu',
      data: {
        auth,
        ...this.getBusinessAccount(),
        ...this.getBalance(),
        ...notiFormData,
        ...this.getDeferredPayment(),
        ...this.getQuota(),
        ...this.getExclusiveAccountInfo()
      }
    })
  }, 800)

  componentWillUnmount = () => {
    const { FormActions } = this.props
    FormActions.clear({ key: 'layoutHeaderMenu' })
  }

  getAhid = () => {
    return jwt.decode(cookies.get('AHID')) || {}
  }

  onChangeFormData = data => {
    const { FormActions } = this.props
    FormActions.set({ key: 'layoutHeaderMenu', data })
  }

  onChangeSearchMenu = targetObject => {
    this.onChangeFormData({ popperOpen: false })
  }

  onClickAccountId = accountId => {
    const { SnackbarActions } = this.props
    copy(accountId)
    SnackbarActions.new({
      key: 'public',
      message: accountId + ' 클립보드에 복사했습니다.'
    })
  }

  onClickQuota = () => {
    const { history } = this.props
    history.push('/quota')
    this.onChangeSearchMenu()
  }

  onClickGotoBalance = () => {
    const { history } = this.props
    if (config.isCoolsms) {
      history.push('/recharge')
    } else {
      history.push('/balance')
    }
    this.onChangeSearchMenu()
  }

  onClickCharge = () => {
    const { PopupActions } = this.props
    PopupActions.new({
      title: '잔액 충전',
      message: (
        <RechargeContainer
          isCoolsms={false}
          onSuccessRecharge={() => {
            PopupActions.close()
          }}
        />
      )
    })
  }

  onClickProfile = () => {
    const { history } = this.props
    history.push('/profile')
    this.onChangeSearchMenu()
  }

  onClickNoticeList = () => {
    const { PopupActions } = this.props
    PopupActions.new({
      key: 'layoutHeaderNotification',
      title: '읽지 않은 알림',
      message: <LayoutHeaderNotificationContainer />
    })
  }

  onClickAccountInfo = () => {
    const { history } = this.props
    history.push('/accounts/selected-account')
    this.onChangeSearchMenu()
  }

  onClickChangeAccount = () => {
    const { history } = this.props
    history.push('/accounts')
    this.onChangeSearchMenu()
  }

  onClickGotoHomePage = () => {
    const { ConfirmActions } = this.props
    const ahid = this.getAhid()
    const message = []
    const isMysite = !isEmptyObject(ahid) && !isEmptyString(ahid?.homepage)
    if (isMysite) {
      message.push(
        `현재 웹사이트는 (주)솔라피에서 제공하는 메시징 서비스 입니다.`
      )
      message.push(
        `"${ahid?.appName}" 제공사에서 설정한 외부 홈페이지로 이동할까요?`
      )
      message.push(`관련이 없는 페이지로 이동 시 관리자에게 신고 부탁드립니다.`)
    } else {
      message.push('홈페이지가 새 창에서 열립니다. 이동할까요?')
    }
    ConfirmActions.show({
      title: '홈페이지 이동',
      message: message.join('\n\n'),
      onConfirm: () => {
        window
          .open(isMysite ? ahid?.homepage : config?.homePage, '_blank')
          .focus()
        this.onChangeSearchMenu()
      }
    })
  }

  onClickLogout = () => {
    const { apiHost: configApiHost } = config
    const ahid = this.getAhid()
    const apiHost = ahid.apiHost || configApiHost
    cookies.remove('CSAK', { path: '/', domain: getCookieDomain() })
    window.location.href = `${apiHost}/oauth2/v1/logout`
  }

  onClickLogin = () => {
    const { history } = this.props
    history.push('/oauth2/login')
    this.onChangeSearchMenu()
  }

  onClickConvertTrial = () => {
    const { history } = this.props
    history.push('/trial/convert')
  }

  onClickCreateExclusiveAccount = () => {
    const { PopupActions } = this.props
    PopupActions.new({
      title: '전용계좌 생성',
      message: <CreateExclusiveAccountContainer />
    })
  }

  // 사업자여부
  getBusinessAccount = () => {
    const { auth } = this.props
    const isBusinessAccount =
      isObject(auth?.verifyCompanyInfo) &&
      !isEmptyObject(auth?.verifyCompanyInfo) &&
      auth?.verifyCompanyInfo?.status === 'APPROVED'
    const businessName = isBusinessAccount
      ? auth?.verifyCompanyInfo?.businessName
      : ''
    const businessNumber = isBusinessAccount
      ? auth?.verifyCompanyInfo?.businessNumber
      : ''
    return {
      isBusinessAccount,
      businessName,
      businessNumber
    }
  }

  getNotifications = async () => {
    const { getNotifications } = this.props
    const { list: notifications = [], nextKey: hasNextNotification = false } =
      getNotifications?.data || {}
    const badgeContent =
      notifications.length > 0
        ? `${notifications.length}${hasNextNotification ? '+' : ''}`
        : 0
    const badgeInvisible = notifications?.length < 1
    // 중요 알림 존재 여부
    let isExistImportantNoti = false
    if (!badgeInvisible) {
      isExistImportantNoti = await this.getImportantNotifications()
    }
    return { badgeContent, badgeInvisible, isExistImportantNoti }
  }

  getImportantNotifications = async () => {
    try {
      const { NotificationActions } = this.props
      const { data } = await NotificationActions.getNotifications(
        { status: 'UNREAD', important: true, limit: 1 },
        { key: 'getImportantUnreadNotifications', cache: false }
      )
      return isArray(data?.list) && data.list.length > 0
    } catch (error) {
      return false
    }
  }

  // 잔액
  getBalance = () => {
    const { getBalance } = this.props
    const { balance = 0, point = 0 } = getBalance?.data || {}
    return { balance, point }
  }

  // 후불여부
  getDeferredPayment = () => {
    const { deferredPayment = {} } = this.props
    const { status: deferredPaymentStatus, data: deferredPaymentData } =
      deferredPayment
    const isDeferredPaymentAccount =
      deferredPaymentStatus !== 404 && deferredPaymentData
    return { isDeferredPaymentAccount }
  }

  // 일발송량
  getQuota = () => {
    const { getEstimateQuota } = this.props
    const { currentQuota = 0, today = {} } = getEstimateQuota?.data || {}
    const { maxCount: useQuota = 0 } = today
    const remainingQuota = currentQuota - useQuota
    return {
      currentQuota,
      remainingQuota: remainingQuota
    }
  }

  // 전용계좌
  getExclusiveAccountInfo = () => {
    const { getBankCode = {}, getExclusiveAccount = {} } = this.props
    const { status, data: { bankCode, accountNumber } = {} } =
      getExclusiveAccount
    const { data: bankCodeData = [] } = getBankCode
    const bankData = bankCodeData.filter(d => d[bankCode])[0] || {}
    const bankName = Object.values(bankData)[0]
    const hasExclusiveAccount =
      status !== 404 &&
      !isEmptyString(accountNumber) &&
      !isEmptyString(bankName)
    return {
      hasExclusiveAccount,
      bankAccountNumber: accountNumber,
      bankName
    }
  }

  onClickCopyText = text => {
    const { SnackbarActions } = this.props
    copy(text)
    SnackbarActions.new({
      message: text + ' 클립보드에 복사했습니다.'
    })
  }

  onOpenBusinessInfo = () => {
    const { PopupActions } = this.props
    PopupActions.new({
      dialogProps: {
        PaperProps: { style: { width: 1200 } }
      },
      message: <AccountBusinessInfoContainer />
    })
  }

  render = () => {
    // hoc 사용 시 proxy 삭제하세요.
    return <LayoutHeaderMenu formData={this.props.formData} proxy={this} />
  }
}

export default withRouter(
  connect(
    bindStateToProps({
      cash: [{ getBalance: 'responseData.getBalance' }],
      quota: [{ getEstimateQuota: 'responseData.getEstimateQuota' }],
      form: [{ formData: 'data.layoutHeaderMenu' }],
      consoleLayout: ['auth'],
      notification: [
        { getNotifications: 'responseData.getUnreadNotifications' }
      ],
      exclusiveAccount: [
        { getBankCode: 'responseData.getBankCode' },
        { getExclusiveAccount: 'responseData.getExclusiveAccount' }
      ],
      deferredPayment: [{ deferredPayment: 'responseData.getDeferredPayment' }]
    }),
    bindActionCreators({
      confirmActions,
      formActions,
      snackbarActions,
      popupActions,
      notificationActions
    })
  )(LayoutHeaderMenuContainer)
)
